import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import {
	AUDIT_LOG_ACTION_DESCRIPTION,
	AUDIT_LOG_ACTION_LABEL,
	AUDIT_LOG_CATEGORY_DESCRIPTION,
	AUDIT_LOG_CATEGORY_LABEL,
	AUDIT_LOG_CATEGORY_STYLES,
	formatAuditMetadataBody,
	formatAuditMetadataSummary,
	formatAuditOccurredAtDetail,
	formatAuditRelativeTime,
	hasAuditMetadata,
	resolveAuditLogIcon,
} from '@/utils/auditLogDisplay';

type AuditTimelineItemProps = {
	entry: AuditLogs.AuditLogResponse;
	isLast: boolean;
	compact?: boolean;
};

const AuditTimelineItem = ({
	entry,
	isLast,
	compact = false,
}: AuditTimelineItemProps) => {
	const styles = AUDIT_LOG_CATEGORY_STYLES[entry.category];
	const Icon = resolveAuditLogIcon(entry.category, entry.action);
	const categoryLabel = AUDIT_LOG_CATEGORY_LABEL[entry.category];
	const actionLabel = AUDIT_LOG_ACTION_LABEL[entry.action];
	const metadataSummary = formatAuditMetadataSummary(
		entry.action,
		entry.metadata,
	);
	const showMetadata = hasAuditMetadata(entry.metadata);
	const occurredAtDetail = formatAuditOccurredAtDetail(entry.occurred_at);

	const metadataTooltip = (
		<pre className="max-h-48 max-w-xs overflow-auto whitespace-pre-wrap break-all text-left text-[11px] leading-relaxed">
			{formatAuditMetadataBody(entry.metadata, entry.action)}
		</pre>
	);

	const dateTooltip = (
		<div className="space-y-0.5 text-left text-xs leading-relaxed">
			{occurredAtDetail.weekday ? (
				<p className="font-semibold">{occurredAtDetail.weekday}</p>
			) : null}
			<p className="tabular-nums">{occurredAtDetail.time}</p>
			<p>{occurredAtDetail.date}</p>
		</div>
	);

	if (compact) {
		const dotBg =
			styles.dot.split(' ').find((c) => c.startsWith('bg-')) ??
			'bg-zinc-400';

		return (
			<div className={cn('relative flex gap-3 pb-4', isLast && 'pb-0')}>
				{!isLast ? (
					<span
						className={cn(
							'absolute left-[5px] top-3 h-[calc(100%-4px)] w-px',
							styles.line,
						)}
					/>
				) : null}
				<span
					className={cn(
						'relative z-[1] mt-1.5 size-2.5 shrink-0 rounded-full ring-2 ring-white',
						dotBg,
					)}
				/>
				<div className="min-w-0 flex-1 space-y-0.5">
					<div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
						<Tooltip
							content={AUDIT_LOG_ACTION_DESCRIPTION[entry.action]}
							className="max-w-xs whitespace-normal text-left"
							interactive
						>
							<span className="cursor-help text-sm font-medium text-zinc-700 underline decoration-zinc-300 decoration-dotted underline-offset-2">
								{actionLabel}
							</span>
						</Tooltip>
						<Tooltip
							content={dateTooltip}
							className="whitespace-normal text-left"
							position="top"
							interactive
						>
							<time
								dateTime={entry.occurred_at}
								className="shrink-0 cursor-help text-[11px] font-medium text-zinc-500 underline decoration-zinc-300 decoration-dotted underline-offset-2"
							>
								{formatAuditRelativeTime(entry.occurred_at)}
							</time>
						</Tooltip>
					</div>
					<Tooltip
						content={AUDIT_LOG_CATEGORY_DESCRIPTION[entry.category]}
						className="max-w-xs whitespace-normal text-left"
						interactive
					>
						<span
							className={cn(
								'inline-flex cursor-help items-center rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wide ring-1',
								styles.badge,
							)}
						>
							{categoryLabel}
						</span>
					</Tooltip>
					{metadataSummary || showMetadata ? (
						<Tooltip
							content={metadataTooltip}
							className="max-w-sm whitespace-normal"
							position="bottom"
							interactive
							hideDelay={200}
						>
							<p className="cursor-help truncate text-[11px] text-zinc-400">
								{metadataSummary ?? 'parâmetros'}
							</p>
						</Tooltip>
					) : null}
				</div>
			</div>
		);
	}

	return (
		<li className="relative flex items-center gap-4 pb-6 last:pb-0">
			{!isLast ? (
				<span
					className={cn(
						'absolute bottom-0 left-[1.125rem] top-9 w-px -translate-x-1/2',
						styles.line,
					)}
					aria-hidden
				/>
			) : null}

			<div
				className={cn(
					'relative z-[1] flex size-9 shrink-0 items-center justify-center rounded-full text-white shadow-sm ring-4',
					styles.dot,
				)}
			>
				<Icon className="size-4" strokeWidth={2.25} aria-hidden />
			</div>

			<div className="flex min-w-0 flex-1 items-center gap-2">
				<Tooltip
					content={AUDIT_LOG_CATEGORY_DESCRIPTION[entry.category]}
					className="max-w-xs whitespace-normal text-left"
					interactive
				>
					<span
						className={cn(
							'inline-flex shrink-0 cursor-help items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1',
							styles.badge,
						)}
					>
						{categoryLabel}
					</span>
				</Tooltip>

				<Tooltip
					content={AUDIT_LOG_ACTION_DESCRIPTION[entry.action]}
					className="max-w-xs whitespace-normal text-left"
					interactive
				>
					<span className="shrink-0 cursor-help text-xs font-semibold text-zinc-900 underline decoration-zinc-300 decoration-dotted underline-offset-2">
						{actionLabel}
					</span>
				</Tooltip>

				<div className="min-w-0 flex-1">
					{metadataSummary || showMetadata ? (
						<Tooltip
							content={metadataTooltip}
							className="max-w-sm whitespace-normal"
							position="bottom"
							interactive
							hideDelay={200}
						>
							<div className="inline-block w-fit max-w-full cursor-help truncate rounded-lg border border-zinc-100 bg-zinc-50/60 px-2 py-0.5 text-[11px] text-zinc-400 transition-colors hover:border-zinc-200 hover:bg-zinc-100/70">
								{metadataSummary ?? 'parâmetros'}
							</div>
						</Tooltip>
					) : null}
				</div>

				<Tooltip
					content={dateTooltip}
					className="whitespace-normal text-left"
					position="top"
					interactive
				>
					<time
						dateTime={entry.occurred_at}
						className="shrink-0 cursor-help whitespace-nowrap text-[11px] font-medium text-zinc-500 underline decoration-zinc-300 decoration-dotted underline-offset-2"
					>
						{formatAuditRelativeTime(entry.occurred_at)}
					</time>
				</Tooltip>
			</div>
		</li>
	);
};

export { AuditTimelineItem };
